// ArchetypePlaybookCard.js — agent-facing widget rendered on the call detail
// page. Fetches the archetype playbook that matches THIS call's archetype
// (from `tfvf_v1_2_shadow_grades`) so consultants see the exact reference
// beside the transcript they just made.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Loader2, ChevronDown, ChevronUp, Sparkles, CheckCircle2, XCircle, AlertCircle, MinusCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARCHETYPE_LABELS = {
  CALLBACK: 'Callback (Warm Follow-up)',
  NEW_ENQUIRY: 'New Enquiry (First Contact)',
  TOUR_FOLLOWUP: 'Tour Follow-up (Post-Visit Close)',
  MEMBER_ADMIN: 'Member Admin',
  PRICE_QUERY: 'Price Query',
  DORMANT_REVIVAL: 'Dormant Revival',
};

const ArchetypePlaybookCard = ({ callId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Playbook Coach state
  const [coachReport, setCoachReport] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calls/${callId}/archetype-playbook`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  // Load any cached coach report (free — no LLM call).
  const loadCached = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/calls/${callId}/playbook-coach`);
      if (r.data?.exists) setCoachReport(r.data);
    } catch { /* silent */ }
  }, [callId]);

  useEffect(() => {
    if (callId) {
      load();
      loadCached();
    }
  }, [callId, load, loadCached]);

  const runCoach = useCallback(async () => {
    setCoachLoading(true); setCoachError('');
    try {
      const r = await axios.post(`${API}/calls/${callId}/playbook-coach`, {}, { timeout: 90000 });
      setCoachReport(r.data);
      setExpanded(true);
    } catch (e) {
      setCoachError(e?.response?.data?.detail || 'Failed to run coach — try again.');
    } finally {
      setCoachLoading(false);
    }
  }, [callId]);

  if (loading) return null; // Silent while loading — no flicker on grading calls.

  // Not graded yet → don't render anything (call was in-progress or fresh).
  if (!data || data.archetype === null) return null;

  const { archetype, playbook } = data;
  const label = ARCHETYPE_LABELS[archetype] || archetype;

  // Archetype is known but the master admin hasn't drafted a playbook for
  // this domain yet — show a soft prompt so we know coverage is missing.
  if (!playbook) {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 flex items-center gap-2"
        data-testid="archetype-playbook-card-empty"
      >
        <BookOpen className="w-4 h-4 shrink-0" />
        <span>
          This is a <b>{label}</b> call — a playbook hasn&apos;t been drafted for your domain yet.
          Ask your admin to draft one at <code className="bg-amber-100 px-1 py-0.5 rounded">/admin/archetype-playbooks</code>.
        </span>
      </div>
    );
  }

  const s = playbook.sections || {};

  return (
    <div
      className="rounded-xl border border-teal-200 bg-teal-50/40 p-4"
      data-testid={`archetype-playbook-card-${archetype}`}
    >
      <div className="w-full flex items-center justify-between gap-2">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          data-testid="archetype-playbook-toggle"
        >
          <BookOpen className="w-4 h-4 text-teal-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              Playbook: {label}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              How top consultants handle this archetype · click to {expanded ? 'hide' : 'view'}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {playbook?.sections && (
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-playbook-sidebar', {
                  detail: { playbook: { archetype, sections: playbook.sections, playbook_version: playbook.version } },
                }));
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] bg-teal-600 hover:bg-teal-500 text-white rounded"
              data-testid="open-playbook-sidebar"
              title="Open the mid-call sidebar for this call"
            >
              <BookOpen className="w-3 h-3" />Sidebar
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="p-1 text-slate-500"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded
              ? <ChevronUp className="w-4 h-4" />
              : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4 text-sm" data-testid="archetype-playbook-body">
          {s.opening_line && (
            <Section title="Opening line">
              <blockquote className="border-l-2 border-teal-500 pl-3 text-slate-700 italic">
                &ldquo;{s.opening_line}&rdquo;
              </blockquote>
            </Section>
          )}

          {Array.isArray(s.checklist) && s.checklist.length > 0 && (
            <Section title="Checklist">
              <ol className="list-decimal pl-5 space-y-1 text-slate-700">
                {s.checklist.map((step, i) => (<li key={i}>{step}</li>))}
              </ol>
            </Section>
          )}

          {/* ── Playbook Coach — LLM comparison of transcript vs checklist ── */}
          <div className="rounded-lg border border-teal-300 bg-white p-3" data-testid="playbook-coach-block">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 text-teal-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-slate-800 text-sm font-semibold">Coach this call against the playbook</div>
                  <div className="text-slate-500 text-[11px]">
                    {coachReport
                      ? `Report generated ${new Date(coachReport.generated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} · click to regenerate`
                      : 'One-click GPT comparison — hits, misses, one thing to try next call.'}
                  </div>
                </div>
              </div>
              <button
                onClick={runCoach}
                disabled={coachLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-semibold rounded-lg shrink-0"
                data-testid="playbook-coach-run"
              >
                {coachLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {coachLoading ? 'Coaching…' : (coachReport ? 'Re-run' : 'Run coach')}
              </button>
            </div>

            {coachError && (
              <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="playbook-coach-error">
                {coachError}
              </div>
            )}

            {coachReport?.report && (
              <div className="mt-3 space-y-3" data-testid="playbook-coach-report">
                <div className="grid md:grid-cols-2 gap-2">
                  {coachReport.report.top_win && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                      <div className="text-emerald-800 font-semibold text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Top win</div>
                      <div className="text-slate-700 text-xs">{coachReport.report.top_win}</div>
                    </div>
                  )}
                  {coachReport.report.top_miss && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-2">
                      <div className="text-amber-800 font-semibold text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Top miss</div>
                      <div className="text-slate-700 text-xs">{coachReport.report.top_miss}</div>
                    </div>
                  )}
                </div>

                {coachReport.report.overall_summary && (
                  <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-700">
                    {coachReport.report.overall_summary}
                  </div>
                )}

                {Array.isArray(coachReport.report.steps) && coachReport.report.steps.length > 0 && (
                  <div>
                    <div className="text-slate-500 text-[10px] uppercase tracking-widest font-semibold mb-1">Step-by-step</div>
                    <div className="space-y-1.5">
                      {coachReport.report.steps.map((st, i) => {
                        const hit = st.hit === true;
                        const partial = st.hit === 'partial';
                        const Icon = hit ? CheckCircle2 : partial ? MinusCircle : XCircle;
                        const color = hit ? 'text-emerald-600' : partial ? 'text-amber-600' : 'text-rose-600';
                        const bg = hit ? 'bg-emerald-50 border-emerald-200' : partial ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200';
                        return (
                          <div key={i} className={`border rounded p-2 ${bg}`} data-testid={`playbook-coach-step-${i}`}>
                            <div className="flex items-start gap-2">
                              <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${color}`} />
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-800 text-xs font-medium">{i + 1}. {st.step_summary}</div>
                                {st.evidence && (
                                  <div className="text-slate-600 text-[11px] italic mt-1">&ldquo;{st.evidence}&rdquo;</div>
                                )}
                                {st.one_line_coaching && (
                                  <div className="text-teal-700 text-[11px] mt-1">→ {st.one_line_coaching}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {coachReport.report.next_call_focus && (
                  <div className="bg-teal-50 border border-teal-200 rounded p-2 text-xs text-teal-900">
                    <span className="font-semibold">Next call, focus on: </span>
                    {coachReport.report.next_call_focus}
                  </div>
                )}
              </div>
            )}
          </div>

          {Array.isArray(s.discovery_questions) && s.discovery_questions.length > 0 && (
            <Section title="Discovery questions (Green Brain)">
              <ul className="list-disc pl-5 space-y-1 text-slate-700">
                {s.discovery_questions.map((q, i) => (<li key={i}>{q}</li>))}
              </ul>
            </Section>
          )}

          {Array.isArray(s.objection_handling) && s.objection_handling.length > 0 && (
            <Section title="Objection handling">
              <div className="space-y-2">
                {s.objection_handling.map((o, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-2.5">
                    <div className="text-amber-700 text-xs font-mono">{o.objection}</div>
                    <div className="text-slate-700 text-sm mt-1">→ {o.response}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {s.close_or_next_step && (
            <Section title="Close / defined next step">
              <p className="text-slate-700">{s.close_or_next_step}</p>
            </Section>
          )}

          {s.disc_notes && (
            <Section title="DISC tone notes">
              <div className="grid md:grid-cols-2 gap-2">
                {Object.entries(s.disc_notes).map(([disc, note]) => (
                  <div key={disc} className="bg-white border border-slate-200 rounded-lg p-2">
                    <div className="text-teal-700 font-mono text-xs">{disc}</div>
                    <div className="text-slate-700 text-xs mt-1">{note}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {Array.isArray(s.do_not_say) && s.do_not_say.length > 0 && (
            <Section title="Do NOT say">
              <ul className="list-disc pl-5 space-y-1 text-rose-800">
                {s.do_not_say.map((x, i) => (<li key={i}>{x}</li>))}
              </ul>
            </Section>
          )}

          <div className="text-[10px] text-slate-400 pt-2 border-t border-teal-100">
            Playbook v{playbook.version} · drafted {playbook.generated_at ? new Date(playbook.generated_at).toLocaleDateString() : '—'}
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h5 className="text-slate-500 text-[10px] uppercase tracking-widest font-semibold mb-1.5">{title}</h5>
    {children}
  </div>
);

export default ArchetypePlaybookCard;
